import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { pct, ratioTone, fmtCount, RATIO_TONES, SEGMENT_COLORS, share } from './objectiveFormat';
import { BLOCKS } from './ObjectiveMatrix';

const ALL = 'ALL';
const CELL = 'tw-px-3 tw-py-2 tw-border-b tw-border-slate-100';

const METRICS = [
  { key: 'meet_schedule_ratio',  short: 'CSD sched.', graded: false },
  { key: 'meet_committed_ratio', short: 'Committed',  graded: true  },
];

// A ratio cell that also draws its own magnitude — with eight CSEs and six
// ratio columns, the bars are what make an outlier findable at a glance.
const RatioCell = ({ stats, metric, borderLeft }) => {
  const ratio = stats?.[metric.key];
  const tone = metric.graded ? ratioTone(ratio) : RATIO_TONES.neutral;
  const barColor = metric.graded ? tone : SEGMENT_COLORS.primary;

  return (
    <td className={`${CELL} ${borderLeft ? 'tw-border-l tw-border-slate-200' : ''} tw-align-middle`}>
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
        <div className="tw-hidden tw-h-1.5 tw-w-12 tw-overflow-hidden tw-rounded-full tw-bg-slate-100 lg:tw-block">
          <div className="tw-h-full" style={{ width: `${share(ratio, 1) * 100}%`, backgroundColor: barColor }} />
        </div>
        <span className="tw-w-9 tw-text-right tw-font-semibold tw-tabular-nums" style={{ color: tone }}>{pct(ratio)}</span>
        <span className="tw-w-9 tw-text-right tw-text-[11px] tw-tabular-nums tw-text-slate-400">{fmtCount(stats?.total)}</span>
      </div>
    </td>
  );
};
RatioCell.propTypes = { stats: PropTypes.object, metric: PropTypes.object.isRequired, borderLeft: PropTypes.bool };

const SortIcon = ({ active, direction }) => {
  if (!active) return null;
  return direction === 'asc'
    ? <FaSortUp size={9} className="tw-ml-1 tw-inline tw-text-primary-500" />
    : <FaSortDown size={9} className="tw-ml-1 tw-inline tw-text-primary-500" />;
};
SortIcon.propTypes = { active: PropTypes.bool, direction: PropTypes.string };

/**
 * Ranking view: every CSE order-entry in-charge side by side.
 *
 * ALL is pinned to the top and excluded from sorting — it is the aggregate, not
 * a competitor, and letting it drift into the middle of a sorted list is how
 * people misread it as one more person's score.
 */
const CseComparisonTable = ({ groups, selectedCse, onSelect }) => {
  const [sort, setSort] = useState({ column: 'jobs', direction: 'desc' });

  const toggleSort = useCallback((column) => {
    setSort((prev) => (prev.column === column
      ? { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      : { column, direction: 'desc' }));
  }, []);

  const { allGroup, rows } = useMemo(() => {
    const all = groups.find((group) => group.cse === ALL);
    const rest = groups.filter((group) => group.cse !== ALL);

    const valueOf = (group) => {
      if (sort.column === 'jobs') return group.jobs ?? 0;
      if (sort.column === 'cse') return group.cse;
      const [blockKey, metricKey] = sort.column.split('.');
      const ratio = group[blockKey]?.[metricKey];
      // Groups with no denominator sort last in both directions — they have no
      // score, so they must never top the ranking.
      return ratio === null || ratio === undefined ? -1 : ratio;
    };

    const sorted = [...rest].sort((a, b) => {
      const left = valueOf(a);
      const right = valueOf(b);
      const cmp = typeof left === 'string' ? left.localeCompare(right) : left - right;
      return sort.direction === 'asc' ? cmp : -cmp;
    });

    return { allGroup: all, rows: sorted };
  }, [groups, sort]);

  const headerButton = (column, label, extraClass = '') => (
    <button
      type="button"
      onClick={() => toggleSort(column)}
      className={`tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-inherit tw-text-inherit hover:tw-text-primary-600 ${extraClass}`}
    >
      {label}
      <SortIcon active={sort.column === column} direction={sort.direction} />
    </button>
  );

  const renderRow = (group, pinned) => (
    <tr
      key={group.cse}
      onClick={() => onSelect(group.cse)}
      aria-selected={group.cse === selectedCse}
      className={`tw-cursor-pointer tw-transition-colors ${
        pinned
          ? 'tw-bg-blue-50/80 tw-font-semibold'
          : group.cse === selectedCse
            ? 'tw-bg-primary-50'
            : 'hover:tw-bg-slate-50/70'
      }`}
    >
      <td className={`${CELL} tw-font-semibold ${pinned ? 'tw-text-primary-700' : 'tw-text-slate-700'}`}>
        {group.cse}
      </td>
      <td className={`${CELL} tw-text-right tw-tabular-nums tw-text-slate-500`}>{fmtCount(group.jobs)}</td>
      {BLOCKS.map((block) => METRICS.map((metric, index) => (
        <RatioCell
          key={`${block.key}-${metric.key}`}
          stats={group[block.key]}
          metric={metric}
          borderLeft={index === 0}
        />
      )))}
    </tr>
  );

  return (
    <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white">
      <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-xs">
        <thead>
          <tr className="tw-bg-slate-50/95">
            <th rowSpan={2} className="tw-border-b tw-border-slate-200 tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              {headerButton('cse', 'CSE OE', 'tw-text-left')}
            </th>
            <th rowSpan={2} className="tw-border-b tw-border-slate-200 tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              {headerButton('jobs', 'Jobs', 'tw-text-right')}
            </th>
            {BLOCKS.map((block) => (
              <th key={block.key} colSpan={METRICS.length} title={block.title} className="tw-border-b tw-border-l tw-border-slate-200 tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
                {block.label}
              </th>
            ))}
          </tr>
          <tr className="tw-bg-slate-50/95">
            {BLOCKS.map((block) => METRICS.map((metric, index) => (
              <th
                key={`${block.key}-${metric.key}`}
                className={`tw-border-b tw-border-slate-200 tw-px-3 tw-py-1.5 tw-text-right tw-font-medium tw-text-slate-400 ${index === 0 ? 'tw-border-l' : ''}`}
              >
                {headerButton(`${block.key}.${metric.key}`, metric.short, 'tw-text-right')}
              </th>
            )))}
          </tr>
        </thead>
        <tbody>
          {allGroup && renderRow(allGroup, true)}
          {rows.map((group) => renderRow(group, false))}
        </tbody>
      </table>
    </div>
  );
};

CseComparisonTable.propTypes = {
  groups:      PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCse: PropTypes.string,
  onSelect:    PropTypes.func.isRequired,
};

export default memo(CseComparisonTable);
